<template>
    <div>
        <div class="level is-vcentered">
            <div class="level-left">
                <h1 class="title is-size-4">HFM Codes</h1>
            </div>
            <div class="level-right">
                <!-- New HFM modal -->
                <button class="level-item button is-primary is-outlined"
                    @click="showAddHfmForm">
                    Add New HFM Code
                </button>
                <router-link class="level-item button is-outlined" to="/Admin">Back to Admin</router-link>
            </div>
        </div>
        <b-notification type="is-danger" v-if="errorMessage" :closable="false">
            {{ errorMessage }}
        </b-notification>
        <!-- Search input -->
        <b-field>
            <b-input
                rounded
                v-model="searchName"
                :data="filteredHfmArray"
                placeholder="search..."
                icon="search"
            >
            </b-input>
        </b-field>
        <!-- HFM Table -->
        <b-table
            class="box"
            :data="searchName ? filteredHfmArray : hfmCodes"
            :paginated="true"
            :per-page="itemsPerPage"
            :current-page.sync="currentPage"
            :pagination-simple="false"
            :pagination-position="'both'"
            :default-sort-direction="'asc'"
            :striped="true"
            :loading="isLoading"
            default-sort="id"
        >
            <template v-slot:bottom-left>
                {{pageInfo}}
            </template>

            <template slot-scope="props">
                <b-table-column field="id" label="ID" width="100" numeric centered sortable>
                    {{ props.row.id }}
                </b-table-column>

                <b-table-column field="code" label="Code" sortable >
                    <span>{{ props.row.code }}</span>

                </b-table-column>
                <b-table-column field="isAuthorised" label="Is Authorised?" centered sortable>
                    <div>
                        <b-icon pack="fas"
                            :icon="props.row.isAuthorised ? 'check' : 'times'"
                            :type="props.row.isAuthorised ? 'is-success' : 'is-danger'">
                        </b-icon>
                        {{ props.row.isAuthorised ? 'Yes' : 'No' }}
                    </div>
                </b-table-column>
                <b-table-column field="country" label="Country" sortable>
                    <span>{{ props.row.country }}</span>
                </b-table-column>

                <b-table-column field="company" label="Company" sortable>
                    <span>{{ props.row.company }}</span>
                </b-table-column>

                <b-table-column label="Edit" width="100">
                    <button class="level-item button is-outlined" @click="showUpdateHfmForm(props.row)">
                        <b-icon
                            icon="edit"
                            type="is-primary"
                        >
                        </b-icon>
                    </button>
                </b-table-column>
                <b-table-column label="Delete" width="100">
                    <button class="level-item button is-outlined" @click="showConfirmDeleteModal(props.row)">
                        <b-icon
                            icon="trash-alt"
                            type="is-danger"
                        >
                        </b-icon>
                    </button>
                </b-table-column>
            </template>
            <!-- Empty Table Template -->
            <template slot="empty">
                <empty-table-content/>
            </template>
        </b-table>
    </div>
</template>

<script>
import axios from '@/services/axios-auth-wrapper';
import HfmForm from '@/components/HfmForm.vue';
import EmptyTableContent from '@/components/EmptyTableContent.vue';

export default {
    name: 'HfmAdmin',
    components: {
        EmptyTableContent
    },
    data () {
        return {
            hfmCodes: [],
            apiUrl: process.env.VUE_APP_CONVERSABILITY_API_URL + '/api/hfmcode/',
            searchName: '',
            isLoading: true,
            errorMessage: '',
            itemsPerPage: 25,
            currentPage: 1
        };
    },
    created () {
        axios.get(this.apiUrl)
            .then(response => {
            // JSON responses are automatically parsed.
                this.hfmCodes = response.data;
            })
            .catch(error => {
                this.errorMessage = error.message;
            })
            .finally(() => {
                this.isLoading = false;
            });
    },
    methods: {
        deleteHfm (hfmToDelete) {
            axios.delete(this.apiUrl + hfmToDelete.id)
                .then(response => {
                    this.hfmCodes = this.hfmCodes.filter(code => code.id !== hfmToDelete.id);
                    this.$buefy.toast.open({
                        message: hfmToDelete.code + ' has been deleted',
                        type: 'is-success'
                    });
                })
                .catch(this.showErrorToast);
        },
        showAddHfmForm () {
            this.$buefy.modal.open({
                parent: this,
                component: HfmForm,
                hasModalCard: true,
                props: {
                    isUpdate: false
                },
                events: {
                    submitEvent: this.addHfm
                }
            });
        },
        addHfm (newHfm) {
            this.hfmCodes.push(newHfm);
            this.$buefy.toast.open({
                message: newHfm.code + ' has been added',
                type: 'is-success'
            });
        },
        showUpdateHfmForm (hfmToEdit) {
            this.$buefy.modal.open({
                parent: this,
                component: HfmForm,
                hasModalCard: true,
                props: {
                    isUpdate: true,
                    hfmToEdit: hfmToEdit
                },
                events: {
                    submitEvent: (updatedHfm) => this.updateHfm(hfmToEdit, updatedHfm)
                }
            });
        },
        updateHfm (hfmToEdit, updatedHfm) {
            Object.assign(hfmToEdit, updatedHfm);
            this.$buefy.toast.open({
                message: updatedHfm.code + ' has been updated',
                type: 'is-success'
            });
        },
        showConfirmDeleteModal (hfmToDelete) {
            this.$buefy.dialog.confirm({
                title: 'Delete HFM',
                message: `Are you sure you want to delete <b>` + hfmToDelete.code + `</b>?`,
                onConfirm: () => this.deleteHfm(hfmToDelete)
            });
        },
        showErrorToast (error) {
            let msg;
            if (error.response && error.response.data) {
                msg = error.response.data;
            } else {
                msg = error.message;
            }
            this.$buefy.toast.open({
                message: msg,
                type: 'is-danger'
            });
        }
    },
    computed: {
        filteredHfmArray () {
            return this.hfmCodes.filter(hfm => {
                return hfm.code
                    .toLowerCase()
                    .includes(this.searchName.toLowerCase()) ||
                    hfm.company
                        .toLowerCase()
                        .includes(this.searchName.toLowerCase()) ||
                    hfm.country
                        .toLowerCase()
                        .includes(this.searchName.toLowerCase());
            });
        },
        pageInfo: function () {
            let totalCodes = this.filteredHfmArray.length;
            let startRange = (this.currentPage - 1) * this.itemsPerPage + 1;
            let endRange = Math.min(this.currentPage * this.itemsPerPage, totalCodes);
            return `Showing ${startRange} to ${endRange} of ${totalCodes} HFM Codes`;
        }
    }
};
</script>

<style lang="scss">
.table td {
    vertical-align: middle;
}
</style>
